import { compact } from 'lodash'
import moment from 'moment/moment'

import { PSApplication } from '../components/app/PSApplication'
import { TagProps } from '../components/app/design/Tag'
import { DataType } from '../types/DataType'
import { ConstructorArgs } from '../types/models'
import { mergeFk } from '../utils'
import { currencyFloat } from '../utils/numbers'
import { BaseRecord } from './BaseRecord'
import { Item } from './Item'
import { Option } from './Option'
import { Promotion } from './Promotion'
import { FKType } from './SQLBuilder'
import { Source } from './Source'
import { Transactable } from './Transactable'
import { Transaction } from './Transaction'
import { User } from './User'

type GiftConstructorArgs = ConstructorArgs<() => Gift>

export class Gift extends Transaction implements Transactable {
    readonly recordType = 'gift'

    readonly iTransactable = true

    memo?: string
    allowSurcharge?: boolean
    status?: string // only settable for new gift certificates [sentQuote, paid] and used to log active ones
    expires?: number // Integer
    markPaidDate?: moment.Moment
    expiredDate?: moment.Moment
    private readonly _balance?: number

    static readonly DEFAULT_REMINDER = '1'
    static readonly GIFT_TOTAL_LIMIT = 100000

    constructor(data?: Partial<GiftConstructorArgs>) {
        super(data)
        if (!data) {
            this.expires = 1800
            this.reminder = Gift.DEFAULT_REMINDER
            return
        }

        this.allowSurcharge = data.allow_surcharge ?? undefined
        this.expires = this.propertyFromIntArg(data.expires)
        this.memo = data.memo ?? undefined
        this.markPaidDate = this.propertyFromTimestampArg(data.mark_paid_date)
        this.expiredDate = this.propertyFromTimestampArg(data.expired_date)
        this.status =
            this.paidDate !== undefined
                ? 'paid'
                : this.cancelledDate !== undefined
                  ? 'canceled'
                  : 'sentQuote'
        this._balance = this.propertyFromCurrencyArg(data.balance)
    }

    clone(overrides?: Partial<GiftConstructorArgs>): Gift {
        return new Gift({ ...this.serializeForClone(), ...overrides })
    }

    static initWithItem(item: Item): Gift {
        const gift = new Gift()
        gift.item = item
        gift.expires = item.expires
        gift.total = item.deposit
        return gift
    }

    static expirationStrings(): Option[] {
        const strings: Option[] = []
        strings.push(new Option(undefined, { id: '', label: 'No expiration' }))
        strings.push(new Option(undefined, { id: '360', label: '1 year' }))
        strings.push(new Option(undefined, { id: '720', label: '2 years' }))
        strings.push(new Option(undefined, { id: '1800', label: '5 years' }))
        return strings
    }

    // todo: implement ComputeFactory.fetchPaidGifts sync
    // get hasGifts(): boolean | undefined {
    //     const me = User.currentUser()
    //     return Gift.loadAllPaidGifts(me, this.owner!).length > 0
    // }

    get amount(): number | undefined {
        if (this.discount && this.discount > 0 && this.total !== undefined)
            return this.total - this.discountAmount
        return this.total
    }

    get gratuityAmount(): number {
        if (!!this.gratuity && this.gratuity !== 0) {
            let gratuity = this.gratuity
            if (this.gratuityType === 'percent') {
                if (this.amount === undefined) gratuity = 0
                else gratuity = this.amount * currencyFloat(this.gratuity / 100)
            }
            return gratuity
        }
        return 0
    }

    get discountAmount(): number {
        if (!!this.discount && this.discount > 0) {
            let discount = this.discount
            if (this.discountType === 'percent') {
                const total = this.total ?? 0
                discount = total * currencyFloat(this.discount / 100)
            }
            return discount
        }
        return 0
    }

    get surchargeAmount(): number {
        if (this.allowSurcharge && this.markPaidDate === undefined) {
            return (this.taxAmount + (this.amount ?? 0)) * (Source.surchargeRate(this.source) ?? 0)
        }
        return 0
    }

    get balance(): number | undefined {
        return !!this.id ? this._balance : this.total
    }

    get taxAmount(): number {
        if (this.tax && this.amount !== undefined)
            return this.tax * currencyFloat(this.amount / 100)
        return 0
    }

    get chargeAmount(): number {
        return (this.amount ?? 0) + this.taxAmount + this.gratuityAmount + this.surchargeAmount
    }

    get askingAmount(): number {
        return (this.amount ?? 0) + this.taxAmount
    }

    get person(): User | undefined {
        if (PSApplication.me().id === this.customer?.id) return this.employee ?? this.vendor
        return Transaction.getPerson(this)
    }

    get paidAmount(): number | undefined {
        return this.paidDate !== undefined ? this.chargeAmount : undefined
    }

    get owedAmount(): number | undefined {
        return this.paidDate !== undefined || this.cancelledDate !== undefined
            ? undefined
            : this.amount
    }

    get statuses(): {
        title: string
        date: moment.Moment | undefined
        intent: TagProps['intent']
    }[] {
        return compact([
            {
                title: 'Sent',
                date: this.createdDate,
                intent: 'info',
            },
            {
                title: 'Viewed',
                date: this.viewedDate,
                intent: 'warning',
            },
            {
                title: this.paidDate ? 'Active (Paid)' : 'Active',
                date: this.paidDate,
                intent: 'success',
            },
            {
                title: 'Expired',
                date: this.expiredDate?.isBefore() ? this.expiredDate : undefined,
                intent: 'danger',
            },
            this.cancelledDate && {
                title: 'Canceled',
                date: this.cancelledDate,
                intent: 'danger',
            },
        ])
    }

    get statusTag(): { label: string; intent: TagProps['intent'] } | null {
        const lastActiveStatus = this.statuses.findLast((status) => status.date !== undefined)
        if (!lastActiveStatus) {
            return null
        }

        return { label: lastActiveStatus.title, intent: lastActiveStatus.intent }
    }

    get statusString(): string {
        if (!this.id && this.status === 'paid') return 'Already paid'
        if (this.cancelledDate) return `Canceled ${this.cancelledDate.format('MMM D, YYYY')}`
        if (this.markPaidDate && this.paymeth)
            return `Marked paid ${this.markPaidDate.format('MMM D, YYYY')} - ${this.paymeth}`
        if (this.markPaidDate) return `Marked paid ${this.markPaidDate.format('MMM D, YYYY')}`
        if (this.paidDate) return `Paid ${this.paidDate.format('MMM D, YYYY')}`
        if (this.id) return `Sent ${this.createdDate!.format('MMM D')} - not paid`
        return 'Requires payment'
    }

    get dashboardStatusString(): string {
        if (this.markPaidDate)
            return `Paid via ${this.paymeth ?? 'other'} - exp ${this.expiredDate?.format('MMM D, YYYY')}`
        if (this.paidDate)
            return `Paid via credit card - exp ${this.expiredDate?.format('MMM D, YYYY')}`
        return `Sent ${this.createdDate!.format('MMM D')} - not paid`
    }

    get expirationString(): string | undefined {
        if (this.expiredDate && this.paidDate) {
            return `expires ${this.expiredDate.format('MMM D, YYYY')}`
        }
        const l = Gift.expirationStrings()
        for (let i = 0; i < l.length; i++) {
            const opt = l[i]
            if (opt.id === (this.expires?.toString() ?? '0')) return opt.label
            if (!this.expires && !opt.id) return opt.label
        }
        return undefined
    }

    get name(): string | undefined {
        return this.item?.name
    }

    toString(): string {
        return this.description
    }

    get description(): string {
        if (this.cancelledDate) return `Canceled on ${this.cancelledDate.format('MMM D')}`
        if (this.id && this.expiredDate) return `expires ${this.expiredDate.format('MMM D, YYYY')}`
        if (this.item && !this.item.expires) return 'no expiration'
        if (this.item && this.item?.expires && this.item.expires % 30)
            return `expires ${this.item.expires} days`
        if (this.item && this.item.expires === 30) return 'expires 1 month'
        return `expires ${(this.item?.expires ?? 0) / 30} months`
    }

    deserialize(data: any) {
        super.deserialize(data)

        if (data.item) this.item = BaseRecord.fromSerializer(data.item, Item)
        if (data.customer) this.customer = BaseRecord.fromSerializer(data.customer, User)
        if (data.promotion) this.promotion = BaseRecord.fromSerializer(data.promotion, Promotion)
        if (data.source) this.source = BaseRecord.fromSerializer(data.source, Source)
        if (data.markPaidDate) this.markPaidDate = moment(data.markPaidDate)
        if (data.expiredDate) this.expiredDate = moment(data.expiredDate)
    }

    static fk: FKType = mergeFk(Transaction.fk, {
        items: ['item'],
        users: ['customer'],
        promotions: ['promotion'],
        sources: ['source'],
    })

    static dataType(): DataType {
        return 'gifts'
    }

    jsonObject(): any {
        const dict = super.jsonObject()
        dict['total'] = this.total ?? 0
        dict['expires'] = this.expires
        if (this.expiredDate) dict['expired_date'] = this.expiredDate
        if (!this.id) dict['status'] = this.status
        dict['memo'] = this.memo ?? ''
        dict['customer'] = this.customer?.id ?? ''
        dict['item'] = this.item?.id ?? ''
        dict['source'] = this.source?.id ?? ''
        dict['channel'] = this.channel ?? ''
        dict['reminder'] = this.reminder ?? Gift.DEFAULT_REMINDER
        dict['promotion'] = this.promotion?.id ?? ''
        dict['discount_type'] = this.discountType ?? ''
        dict['gratuity'] = this.gratuity ?? ''
        dict['gratuity_type'] = this.gratuity ? this.gratuityType : ''
        dict['allow_surcharge'] = this.allowSurcharge
        return dict
    }

    get item(): Item | undefined {
        return this.getForeignRecord('item', Item)
    }
    set item(item: Item | undefined) {
        this.setForeignRecord('item', item)
    }
    get customer(): User | undefined {
        return this.getForeignRecord('customer', User)
    }
    set customer(customer: User | undefined) {
        this.setForeignRecord('customer', customer)
    }
    get promotion(): Promotion | undefined {
        return this.getForeignRecord('promotion', Promotion)
    }
    set promotion(promotion: Promotion | undefined) {
        this.setForeignRecord('promotion', promotion)
    }
    get source(): Source | undefined {
        return this.getSourceRecord('source')
    }
    set source(source: Source | undefined) {
        this.setForeignRecord('source', source)
    }
}
